import { useState } from 'react'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { validNumber, validExpirationDate, validCvv } from '@/utils/payments'

export const usePaymentValidation = () => {
  const [validCVCLength, setValidCVCLength] = useState(null)
  const acceptedCards = ['visa', 'mastercard', 'american-express', 'unionpay']
  const { t } = useTranslation()
  const isCardNumberValid = (cardNumber: string) => {
    const { card, isValid }: any = validNumber(cardNumber)
    setValidCVCLength(card ? card?.code.size : null)
    return acceptedCards.includes(card?.type) && isValid
  }

  const isExpirationValid = (expiration: string) => {
    const { isValid: isExpValid } = validExpirationDate(expiration)
    return isExpValid
  }

  const isSecurityCodeValid = (securityCode: string) => {
    if (validCVCLength) {
      const { isValid: isCodeValid } = validCvv(securityCode, validCVCLength)
      return isCodeValid
    }
    return true
  }

  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .required()
      .matches(/^[aA-zZ\s]+$/, 'Can only contain letters'),
    last_name: Yup.string()
      .required()
      .matches(/^[aA-zZ\s]+$/, 'Can only contain letters'),
    street1: Yup.string().required(),
    street2: Yup.string(),
    city: Yup.string().required(),
    state: Yup.string().required(),
    country: Yup.string().required(),
    zip: Yup.string().required(),
    card_number: Yup.string()
      .required()
      .test(
        'valid card',
        t('r.payment.edit.invalid_card_number.error', 'Invalid card number'),
        value => isCardNumberValid(value ? value : '')
      ),
    expiration_date: Yup.string()
      .required()
      .test('valid expiration', t('b.checkout.error.expiry', 'Invalid date'), value =>
        isExpirationValid(value ? value : '')
      ),
    security_code: Yup.string()
      .required()
      .min(3, t('b.checkout.error.cvc', 'Invalid CVC'))
      .test('cvc', t('b.checkout.error.cvc', 'Invalid CVC'), value =>
        isSecurityCodeValid(value ? value : '')
      ),

    default_payment_method: Yup.boolean(),
    save_payment_method: Yup.boolean(),
    save_billing_address: Yup.boolean()
  })
  return validationSchema
}

export const initialValues = {
  default_payment_method: true,
  payment_method: 'stripe',
  first_name: '',
  last_name: '',
  street1: '',
  street2: '',
  city: '',
  state: '',
  country: 'ca',
  zip: '',
  card_number: '',
  expiration_date: '',
  security_code: '',
  save_payment_method: false,
  save_billing_address: false
}

export type PaymentFormValuesType = {
  default_payment_method: boolean
  payment_method: string
  first_name: string
  last_name: string
  street1: string
  street2: string
  city: string
  state: string
  country: string
  zip: string
  card_number: string
  expiration_date: string
  security_code: string
  save_payment_method: boolean
  save_billing_address: boolean
}
